import { FlashList } from '@shopify/flash-list';
import { remove } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { ScrollView, View, StyleSheet, } from 'react-native';
import { useSharedValue, withTiming } from 'react-native-reanimated';
import { AppView, rem, SCROLL_INDICATOR_INSETS } from 'design-system';
import { useChipGroup } from 'design-system/ChipGroup';
import SelectorControllableReanimated, { DEFAULT_SPACE_BETWEEN, } from 'design-system/SelectorControllableReanimated';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { RewardListItem } from 'features/rewards/components/RewardListItem';
import { filterRewardsBy, useRewards } from 'features/rewards/hooks/useRewards';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { RewardTypes } from 'reducers/types';
import Amplitude from 'utils/amplitude';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { REWARD_TYPES } from './utils';
import { isWeb } from '../../constants';
const strings = {
    title: 'Cashback',
};
const SELECTION_STEP = 1;
const SPACE_BETWEEN = DEFAULT_SPACE_BETWEEN;
const estimateWidth = (label) => rem(label.length * 8 + 32);
const CHIP_WIDTHS = {
    ALL: rem(51),
    [RewardTypes.Home]: rem(75),
    [RewardTypes.Fashion]: rem(87),
    [RewardTypes.Food]: rem(123),
    [RewardTypes.Health]: rem(146),
    [RewardTypes.Entertainment]: rem(135),
    [RewardTypes.Tech]: rem(67),
    [RewardTypes.Beauty]: rem(82),
    [RewardTypes.Travel]: rem(75),
};
const chipsData = [
    {
        id: 'ALL',
        text: 'All',
    },
    REWARD_TYPES[RewardTypes.Home],
    REWARD_TYPES[RewardTypes.Fashion],
    REWARD_TYPES[RewardTypes.Food],
    REWARD_TYPES[RewardTypes.Health],
    REWARD_TYPES[RewardTypes.Entertainment],
    REWARD_TYPES[RewardTypes.Tech],
    REWARD_TYPES[RewardTypes.Beauty],
    REWARD_TYPES[RewardTypes.Travel],
];
const keyExtractor = (item) => item.id.toString();
const HIDE_CHIPS = [
    RewardTypes.Popular,
    RewardTypes.TopOffers,
    RewardTypes.NewOffers,
];
// FlashList keeps the first visible item in place when the data changes, so switching to
// a longer category on web opened it part way down. Same as the Pots list.
const MAINTAIN_VISIBLE_CONTENT_POSITION = isWeb
    ? { disabled: true }
    : undefined;
export const RewardCategoryScreen = withAnimatedNavigationHeader(({ route, onScrollY }) => {
    const rewardType = route.params?.rewardType;
    const hideChips = HIDE_CHIPS.includes(rewardType);
    const colors = useColors();
    const chipsScrollRef = useRef(null);
    const scrollX = useSharedValue(0);
    const { paddingHorizontal, paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const headerPadding = paddingHorizontal + rem(16);
    const [viewportWidth, setViewportWidth] = useState(0);
    const viewportWidthRef = useRef(0);
    viewportWidthRef.current = viewportWidth;
    const onChipsLayout = useCallback((e) => {
        setViewportWidth(e.nativeEvent.layout.width);
    }, []);
    const headerFullBleedStyle = useMemo(() => ({ marginHorizontal: -paddingHorizontal }), [paddingHorizontal]);
    const { normalizedByTypeRewards, sortedByTypeRewards, openReward, hasRewards, fetchRewards, } = useRewards();
    // Opened from a link or a reload, the screen comes before the rewards are fetched
    useEffect(() => {
        if (!hasRewards) {
            fetchRewards();
        }
    }, [fetchRewards, hasRewards]);
    const onOpenReward = useCallback((reward) => {
        Amplitude.logEvent('Open Reward', { source: 'rewards', reward });
        openReward(reward);
    }, [openReward]);
    const initialSelectedChip = React.useMemo(() => chipsData.find((c) => c.id === rewardType), [rewardType]);
    // Empty until the rewards load, which on web happens after the screen opens
    // when it is reloaded or opened from a link
    const [rewards, setRewards] = useState(normalizedByTypeRewards[rewardType] ?? []);
    const { selectedChips, setSelectedChips } = useChipGroup(initialSelectedChip && [initialSelectedChip], false);
    const visibleChips = useMemo(() => chipsData.filter((chip) => chip.id === 'ALL' ||
        (chip.id &&
            normalizedByTypeRewards[chip.id]?.length)), [normalizedByTypeRewards]);
    const sections = useMemo(() => visibleChips.map((chip) => {
        const Icon = chip.icon;
        return {
            id: chip.id ?? chip.text,
            text: chip.text,
            leftIcon: Icon ? (<Icon color={colors.text.secondary} width={16} height={16}/>) : undefined,
            width: CHIP_WIDTHS[chip.id ?? chip.text]
                ? CHIP_WIDTHS[chip.id ?? chip.text] + (Icon ? rem(16 + 8) : 0)
                : estimateWidth(chip.text),
        };
    }), [visibleChips, colors]);
    const offsets = useMemo(() => sections.map((_, index) => sections
        .slice(0, index)
        .reduce((sum, s) => sum + s.width + SPACE_BETWEEN, 0)), [sections]);
    const selectorWidth = useMemo(() => sections.reduce((sum, s) => sum + s.width, 0) +
        SPACE_BETWEEN * Math.max(sections.length - 1, 0), [sections]);
    const selectedIndex = useMemo(() => visibleChips.findIndex((c) => c.id === selectedChips[0]?.id), [visibleChips, selectedChips]);
    const setSelection = useCallback((index) => {
        const chip = visibleChips[index];
        if (!chip) {
            return;
        }
        if (selectedChips.length === 1 && selectedChips[0].id === chip.id) {
            return;
        }
        setSelectedChips([chip]);
    }, [selectedChips, setSelectedChips, visibleChips]);
    useEffect(() => {
        if (hideChips) {
            setRewards(normalizedByTypeRewards[rewardType] ?? []);
            return;
        }
        const chipTypes = selectedChips.map((c) => c.id);
        const all = remove(chipTypes, (ct) => ct === 'ALL')[0];
        const filteredRewards = all
            ? sortedByTypeRewards
            : filterRewardsBy({
                rewards: sortedByTypeRewards,
                rewardTypes: chipTypes,
            });
        setRewards(filteredRewards);
    }, [
        hideChips,
        normalizedByTypeRewards,
        rewardType,
        selectedChips,
        sortedByTypeRewards,
    ]);
    // Offset that centers the chip at `index` within the viewport.
    const getScrollX = useCallback((index) => {
        const section = sections[index];
        if (!section) {
            return 0;
        }
        return (offsets[index] +
            section.width / 2 -
            (viewportWidth / 2 - headerPadding));
    }, [headerPadding, offsets, sections, viewportWidth]);
    // Picking another category starts its list from the top, rather than keeping the
    // previous list's scroll offset in a list of a different length
    const listRef = useRef(null);
    const previousSelectedIndex = useRef(selectedIndex);
    useEffect(() => {
        if (previousSelectedIndex.current === selectedIndex) {
            return;
        }
        previousSelectedIndex.current = selectedIndex;
        listRef.current?.scrollToOffset({ offset: 0, animated: false });
    }, [selectedIndex]);
    const pillInitialized = useRef(false);
    const didInitialScroll = useRef(false);
    useEffect(() => {
        if (selectedIndex < 0) {
            return;
        }
        const target = selectedIndex * SELECTION_STEP;
        const isPillInitial = !pillInitialized.current;
        pillInitialized.current = true;
        scrollX.value = isPillInitial ? target : withTiming(target);
        if (viewportWidth === 0) {
            return;
        }
        const isScrollInitial = !didInitialScroll.current;
        didInitialScroll.current = true;
        chipsScrollRef.current?.scrollTo({
            x: getScrollX(selectedIndex),
            animated: !isScrollInitial,
        });
    }, [getScrollX, scrollX, selectedIndex, viewportWidth]);
    const selectedIndexRef = useRef(selectedIndex);
    selectedIndexRef.current = selectedIndex;
    const getScrollXRef = useRef(getScrollX);
    getScrollXRef.current = getScrollX;
    const onChipsContentSizeChange = useCallback(() => {
        const index = selectedIndexRef.current;
        if (didInitialScroll.current ||
            index < 0 ||
            viewportWidthRef.current === 0) {
            return;
        }
        didInitialScroll.current = true;
        chipsScrollRef.current?.scrollTo({
            x: getScrollXRef.current(index),
            animated: false,
        });
    }, []);
    const renderItem = useCallback(({ item, index }) => (<RewardListItem index={index} itemsLength={rewards.length} reward={item} onPress={onOpenReward}/>), [onOpenReward, rewards.length]);
    const renderHeader = useMemo(() => (<View style={headerFullBleedStyle}>
            {!hideChips && (<ScrollView ref={chipsScrollRef} horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="always" onLayout={onChipsLayout} onContentSizeChange={onChipsContentSizeChange} contentContainerStyle={[
                styles.chipGroup,
                { paddingHorizontal: headerPadding },
            ]}>
                <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={SELECTION_STEP} setSelection={setSelection} selectorStyle={styles.selectorStyle} itemContainerStyle={styles.itemContainerStyle} style={{ width: selectorWidth }}/>
              </ScrollView>)}
          </View>), [
        headerPadding,
        headerFullBleedStyle,
        hideChips,
        onChipsContentSizeChange,
        onChipsLayout,
        scrollX,
        sections,
        selectorWidth,
        setSelection,
    ]);
    const onScroll = useCallback((event) => {
        onScrollY(event.nativeEvent.contentOffset.y);
    }, [onScrollY]);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle, styles.list);
    return (<AppView withFrame={false}>
          <FlashList ref={listRef} data={rewards} maintainVisibleContentPosition={MAINTAIN_VISIBLE_CONTENT_POSITION} keyExtractor={keyExtractor} renderItem={renderItem} onScroll={onScroll} ListHeaderComponent={renderHeader} showsVerticalScrollIndicator scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle} ListHeaderComponentStyle={styles.header}/>
        </AppView>);
}, ({ route, colors }) => {
    const rewardType = route.params?.rewardType;
    const title = HIDE_CHIPS.includes(rewardType)
        ? REWARD_TYPES[rewardType].text
        : strings.title;
    return {
        title,
        headerColorOutputRange: [
            colors.background.dark,
            colors.cards.onDark,
        ],
    };
});
const styles = StyleSheet.create({
    chipGroup: {
        marginVertical: rem(8),
    },
    header: {
        paddingBottom: rem(8),
    },
    list: {
        paddingTop: rem(8),
    },
    selectorStyle: {
        borderRadius: rem(8),
        justifyContent: 'center',
        padding: 0,
        margin: 0,
    },
    itemContainerStyle: {
        padding: 0,
        margin: 0,
    },
});
